import React, { useCallback, useEffect, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { baseUrl } from "../../../services/config";
import { PremissionChecker } from "../../../services/PremissionChecker";

function DefineCRMResultStatus() {
  const alert = useAlert();
  const user = useSelector(state => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [resultStatuses, setResultStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState(null);
  const [name, setName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach(item => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert]
  );

  const getResultStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/crm-result-statuses`, {
        headers: { Authorization: `Bearer ${authtoken}` }
      });
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to load CRM result statuses");
        return;
      }
      setResultStatuses(data?.payload || []);
    } catch (error) {
      alert.error("Unable to load CRM result statuses");
    } finally {
      setLoading(false);
    }
  }, [alert, authtoken, showErrors]);

  useEffect(() => {
    getResultStatuses();
  }, [getResultStatuses]);

  const closeModal = () => {
    setModal(null);
    setName("");
    setSelectedId(null);
  };

  const saveResultStatus = async () => {
    if (!name.trim()) {
      alert.error("Result Status Name Is Required");
      return;
    }

    const isEditing = modal === "edit";
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-result-statuses${isEditing ? `/${selectedId}` : ""}`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`
          },
          body: JSON.stringify({ name: name.trim() })
        }
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(
          data,
          `Unable to ${isEditing ? "update" : "add"} result status`
        );
        return;
      }
      alert.success(
        `Result status successfully ${isEditing ? "updated" : "added"}`
      );
      closeModal();
      getResultStatuses();
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} result status`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteResultStatus = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-result-statuses/${selectedId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` }
        }
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete result status");
        return;
      }
      alert.success(data?.payload || "Result status successfully deleted");
      closeModal();
      getResultStatuses();
    } catch (error) {
      alert.error("Unable to delete result status");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      {PremissionChecker(user, "crm_result_status.create") && (
        <button
          className="btn btn-primary mr-2"
          onClick={() => setModal("add")}
        >
          Add CRM Result Status
        </button>
      )}

      <Table className="mt-5">
        <thead>
          <tr className="text-center">
            <th>ID</th>
            <th>Result Status Name</th>
            {PremissionChecker(user, "crm_result_status.update") && (
              <th>Update</th>
            )}
            {PremissionChecker(user, "crm_result_status.delete") && (
              <th>Delete</th>
            )}
          </tr>
        </thead>
        <tbody>
          {resultStatuses.map(resultStatus => (
            <tr className="text-center" key={resultStatus.id}>
              <td>{resultStatus.id}</td>
              <td>{resultStatus.name}</td>
              {PremissionChecker(user, "crm_result_status.update") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(resultStatus.id);
                      setName(resultStatus.name || "");
                      setModal("edit");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Write.svg"
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
              {PremissionChecker(user, "crm_result_status.delete") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(resultStatus.id);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg"
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {loading && (
        <div className="row justify-content-center">
          <Spinner animation="border" />
        </div>
      )}

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="md"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} CRM Result Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-control-label">
            <b>
              Enter Result Status Name <span style={{ color: "red" }}>*</span>
            </b>
          </label>
          <input
            className="form-control"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="Enter Result Status Name"
          />
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button
              className="btn btn-primary w-100px"
              onClick={saveResultStatus}
            >
              {modal === "edit" ? "Update" : "Add"}
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this CRM result status?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteResultStatus}>
              Delete
            </button>
          )}
          <button className="btn btn-danger btn_width" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}

export default DefineCRMResultStatus;
